import { useRef, useState } from "react";

/**
 * Single image uploader with preview.
 * - Shows a click-to-upload placeholder when empty
 * - Replaces itself with the image preview once selected
 * - Cross button top-right removes the image
 *
 * Props:
 *   onChange(file | null) — called whenever the selection changes
 *   label               — optional label text shown inside the placeholder
 *   bgColor             — background color for the preview area
 */
export default function SingleImageUploader({ onChange, label = "Click to upload", bgColor = "#ffffff", position = "solo" }) {
	const [preview, setPreview] = useState(null);
	const inputRef = useRef(null);

	const handleFile = (file) => {
		if (!file) return;
		const url = URL.createObjectURL(file);
		setPreview(url);
		onChange?.(file);
	};

	const handleInputChange = (e) => {
		handleFile(e.target.files[0] ?? null);
	};

	const handleRemove = (e) => {
		e.stopPropagation();
		if (preview) URL.revokeObjectURL(preview);
		setPreview(null);
		onChange?.(null);
		// Reset so the same file can be re-selected
		if (inputRef.current) inputRef.current.value = "";
	};

	// Border-radius based on position in a joined group
	const radius = {
		solo:  "rounded-lg",
		left:  "rounded-l-lg rounded-r-none",
		right: "rounded-r-lg rounded-l-none",
	}[position] ?? "rounded-lg";

	return (
		<div style={{ width: 180 }} className="flex flex-col">
			<input
				ref={inputRef}
				type="file"
				accept="image/*"
				className="hidden"
				onChange={handleInputChange}
			/>

			{preview ? (
				/* ── Preview state ── */
				<div
					className={`relative flex-1 overflow-hidden border border-gray-200 cursor-pointer group ${radius} ${position === "right" ? "border-l-0" : ""}`}
					style={{ backgroundColor: bgColor, minHeight: 110 }}
					onClick={() => inputRef.current?.click()}
				>
					<img
						src={preview}
						alt="preview"
						className="w-full h-full object-contain"
						style={{ display: "block" }}
					/>

					{/* Remove button */}
					<button
						type="button"
						onClick={handleRemove}
						className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white rounded-full text-xs font-bold shadow transition-opacity opacity-80 group-hover:opacity-100"
						title="Remove image"
					>
						✕
					</button>

					{/* Hover overlay hint */}
					<div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors pointer-events-none" />
				</div>
			) : (
				/* ── Empty / upload state ── */
				<button
					type="button"
					onClick={() => inputRef.current?.click()}
					className={`flex-1 w-full flex flex-col items-center justify-center gap-1.5 border-2 border-dashed border-gray-300 hover:border-indigo-400 hover:bg-indigo-50 transition-colors text-gray-400 hover:text-indigo-500 ${radius} ${position === "right" ? "border-l-0" : ""}`}
					style={{ minHeight: 110 }}
				>
					<svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
						<path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
					</svg>
					<span className="text-xs font-medium">{label}</span>
				</button>
			)}
		</div>
	);
}
